import { useMemo } from 'react';

import {
    ERROR_CONTENT_HEIGHT,
    INPUT_HEIGHT,
    KEYBOARD_HEIGHT,
    QUICK_AMOUNTS_HEIGHT,
    RaceToPlaceAmountSelectorType,
    TO_WIN_CONTENT_HEIGHT,
    spacing,
} from '../race-to-place-constants';

type UseRaceToPlaceAmountStateParams = {
    selector: RaceToPlaceAmountSelectorType;
    amount: number;
    cash: number;
    maxEntryAmount: number;
    errorMessage?: string;
};

export type RaceToPlaceAmountHeights = {
    selectorHeight: number;
    errorLabelHeight: number;
    errorOrSpacerHeight: number;
    toWinLabelHeight: number;
    keyboardTotalHeight: number;
    selectorToButtonGap: number;
};

export type RaceToPlaceAmountVisibility = {
    showError: boolean;
    showErrorOrSpacer: boolean;
    showToWin: boolean;
    showDepositButton: boolean;
    showSelectorToButtonGap: boolean;
};

export type RaceToPlaceAmountState = {
    visibility: RaceToPlaceAmountVisibility;
    heights: RaceToPlaceAmountHeights;
};

/**
 * Derives visibility flags, heights, and UI state for the amount selection section.
 * Centralizes logic used by both the height calculation hook and the UI component.
 * Eases animation and layout calculations by providing all necessary state in one place.
 *
 * Amount section layout (top to bottom):
 *
 * Quick Amounts:
 *   - quick amounts selector (36)
 *   - [error]        error label (8 + 20 + 16)
 *   - [valid amount] to-win label (16 + 20 + 16)
 *   - [otherwise]    gap (16)
 *   - button (44)
 *
 * Number Pad:
 *   - input (64)
 *   - [error] error label (8 + 20 + 12)
 *   - [else]  spacer (16)
 *   - keyboard (196 + 16)
 *   - button (44)
 */
export const useRaceToPlaceAmountState = ({
    selector,
    amount,
    cash,
    maxEntryAmount,
    errorMessage,
}: UseRaceToPlaceAmountStateParams): RaceToPlaceAmountState => {
    const isQuickAmounts = selector === 'quick-amounts';
    const isNumberPad = !isQuickAmounts;

    // Determine what to show
    const insufficientFunds = amount > cash && cash < maxEntryAmount;
    const showDepositButton = !!errorMessage && insufficientFunds;
    const showError = !!errorMessage && amount > 0;
    const showToWin = amount > 0 && isQuickAmounts && !errorMessage && !showDepositButton;

    // Visibility flags for animated sections
    const showErrorOrSpacer = isNumberPad || showError;
    const showSelectorToButtonGap = isQuickAmounts && !showToWin && !showError;

    // Height calculations
    const selectorHeight = isQuickAmounts ? QUICK_AMOUNTS_HEIGHT : INPUT_HEIGHT;

    const errorLabelHeight =
        spacing.SELECTOR_TO_ERROR +
        ERROR_CONTENT_HEIGHT +
        (isNumberPad ? spacing.ERROR_TO_KEYBOARD : spacing.ERROR_TO_BUTTON);

    const errorOrSpacerHeight = isNumberPad
        ? showError
            ? errorLabelHeight
            : spacing.INPUT_TO_KEYBOARD
        : showError
        ? errorLabelHeight
        : 0;

    const toWinLabelHeight = spacing.SELECTOR_TO_TO_WIN + TO_WIN_CONTENT_HEIGHT + spacing.TO_WIN_TO_BUTTON;

    const keyboardTotalHeight = KEYBOARD_HEIGHT + spacing.KEYBOARD_TO_BUTTON;

    const selectorToButtonGap = spacing.SELECTOR_TO_BUTTON;

    return useMemo(
        () => ({
            visibility: {
                showError,
                showErrorOrSpacer,
                showToWin,
                showDepositButton,
                showSelectorToButtonGap,
            },
            heights: {
                selectorHeight,
                errorLabelHeight,
                errorOrSpacerHeight,
                toWinLabelHeight,
                keyboardTotalHeight,
                selectorToButtonGap,
            },
        }),
        [
            showError,
            showErrorOrSpacer,
            showToWin,
            showDepositButton,
            showSelectorToButtonGap,
            selectorHeight,
            errorLabelHeight,
            errorOrSpacerHeight,
            toWinLabelHeight,
            keyboardTotalHeight,
            selectorToButtonGap,
        ]
    );
};
